/**
 * <new-hover-cards>
 * ---------------------------------------------------------------------------
 * Dawn-style carousel custom element backed by Swiper 11.
 *
 * Responsibilities:
 *   - Request Swiper through the shared dependency loader.
 *   - Initialize a horizontal free-mode carousel with fixed-width slides.
 *   - Sync nav button availability via data-hidden (CSS handles visuals).
 *   - Expose data-has-overflow on the root so CSS can hide nav when not needed.
 *
 * The Liquid template owns the structure:
 *   new-hover-cards.hover-carousel
 *     .hover-carousel__viewport.swiper
 *       .hover-carousel__track.swiper-wrapper
 *         .hover-carousel__slide.swiper-slide * n
 *     button.hover-carousel__nav--prev   ← siblings so overflow on viewport
 *     button.hover-carousel__nav--next     cannot clip them
 */

const SELECTOR = {
  viewport: ".hover-carousel__viewport",
  track: ".hover-carousel__track",
  prev: ".hover-carousel__nav--prev",
  next: ".hover-carousel__nav--next",
};

class NewHoverCards extends HTMLElement {
  constructor() {
    super();
    this._swiper = null;
    this._viewport = null;
    this._track = null;
    this._prev = null;
    this._next = null;
    this._section = null;
    this._revealObserver = null;
    this._onWindowResize = this._onWindowResize.bind(this);
    this._syncNav = this._syncNav.bind(this);
  }

  connectedCallback() {
    this._viewport = this.querySelector(SELECTOR.viewport);
    this._track = this.querySelector(SELECTOR.track);
    this._prev = this.querySelector(SELECTOR.prev);
    this._next = this.querySelector(SELECTOR.next);
    this._section = this.closest(".section-settings");
    if (this._section) this._section.classList.add("has-hover-cards");

    if (!this._viewport) return;

    this._observeReveal();
    window.loadSwiper()
      .then(() => {
        if (this.isConnected) this._init();
      })
      .catch((error) => console.error('[new-hover-cards] Unable to load Swiper.', error));
  }

  disconnectedCallback() {
    this._destroySwiper();
    if (this._revealObserver) {
      this._revealObserver.disconnect();
      this._revealObserver = null;
    }
    window.removeEventListener("resize", this._onWindowResize);
  }

  _observeReveal() {
    const target = this._section || this;

    if (!("IntersectionObserver" in window)) {
      target.classList.add("is-hover-cards-visible");
      this.classList.add("is-visible");
      return;
    }

    this._revealObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        target.classList.toggle("is-hover-cards-visible", entry.isIntersecting);
        this.classList.toggle("is-visible", entry.isIntersecting);
      });
    }, {
      rootMargin: "0px 0px -18% 0px",
      threshold: 0.2,
    });

    this._revealObserver.observe(target);
  }

  _destroySwiper() {
    if (this._swiper) {
      try {
        this._swiper.destroy(true, true);
      } catch (err) {
        /* ignore */
      }
      this._swiper = null;
    }
  }

  _init() {
    if (!this._viewport || typeof window.Swiper !== "function") return;
    if (this._swiper) return;

    /**
     * Swiper 11 behavior notes:
     *   - freeMode.enabled must be set explicitly; passing only
     *     { momentumRatio, sticky } leaves it disabled (default false).
     *   - watchOverflow: false avoids auto-locking (which clamps drag) when
     *     content is only marginally larger than viewport. We decide nav
     *     visibility ourselves via _syncNav().
     *   - slidesPerView: 'auto' reads real slide widths (CSS-owned); numeric
     *     values would fight the `width: var(--hc-item-width) !important` rule.
     *   - spaceBetween: 0 because CSS controls the gap via margin-right; this
     *     keeps Swiper's measurements consistent with what is rendered.
     */
    this._swiper = new window.Swiper(this._viewport, {
      slidesPerView: "auto",
      spaceBetween: 0,
      speed: 400,
      autoHeight: this._viewport.classList.contains('hover-carousel--below'),
      simulateTouch: true,
      grabCursor: true,
      watchOverflow: false,
      resistanceRatio: 0.65,
      freeMode: {
        enabled: true,
        sticky: false,
        momentum: true,
        momentumRatio: 0.7,
        momentumVelocityRatio: 0.7,
        momentumBounce: true,
      },
      navigation: this._prev && this._next
        ? { prevEl: this._prev, nextEl: this._next, disabledClass: "is-disabled" }
        : false,
      on: {
        init: this._syncNav,
        resize: this._syncNav,
        slideChange: this._syncNav,
        reachBeginning: this._syncNav,
        reachEnd: this._syncNav,
        fromEdge: this._syncNav,
        progress: this._syncNav,
        setTranslate: this._syncNav,
        transitionEnd: this._syncNav,
        touchEnd: this._syncNav,
      },
    });

    window.addEventListener("resize", this._onWindowResize, { passive: true });
    this._syncNav();
  }

  _onWindowResize() {
    if (!this._swiper) return;
    this._swiper.update();
    this._syncNav();
  }

  _setNavDisabled(button, disabled) {
    if (!button) return;

    button.toggleAttribute("data-hidden", disabled);
    button.classList.toggle("is-disabled", disabled);
    button.setAttribute("aria-disabled", String(disabled));
    button.tabIndex = disabled ? -1 : 0;
    button.disabled = disabled;
  }

  /**
   * Mirror the real scrollable range onto DOM attributes:
   *   - <new-hover-cards data-has-overflow="true"> when the track can scroll.
   *   - <button data-hidden="true"> when the button is at an edge.
   * CSS reads these attributes to fade the buttons in/out.
   */
  _syncNav() {
    const swiper = this._swiper;
    if (!swiper) return;

    const viewportSize = this._viewport?.clientWidth || swiper.size || 0;
    const contentSize = this._track?.scrollWidth || swiper.virtualSize || 0;
    const hasOverflow = contentSize - viewportSize > 1;

    if (hasOverflow) {
      this.setAttribute("data-has-overflow", "true");
    } else {
      this.removeAttribute("data-has-overflow");
    }

    const translate = typeof swiper.getTranslate === "function" ? swiper.getTranslate() : (swiper.translate || 0);
    const swiperMaxOffset = typeof swiper.maxTranslate === "function" ? Math.abs(swiper.maxTranslate()) : 0;
    const domMaxOffset = Math.max(0, contentSize - viewportSize);
    const maxOffset = Math.max(swiperMaxOffset, domMaxOffset);
    const currentOffset = Math.min(maxOffset, Math.max(0, -translate));
    const edgeTolerance = 2;
    const isBeginning = currentOffset <= edgeTolerance;
    const isEnd = maxOffset - currentOffset <= edgeTolerance;

    this._setNavDisabled(this._prev, !hasOverflow || isBeginning);
    this._setNavDisabled(this._next, !hasOverflow || isEnd);
  }
}

if (!customElements.get("new-hover-cards")) {
  customElements.define("new-hover-cards", NewHoverCards);
}
